import { canUseDOM } from '@/utils/dom-utils';
import { concatUrls } from '@/utils/uri-utils';
import config from '@/temp/config';
import { store } from '@/lib/SitecoreJssStorePlugin';

const DEFAULT_DESC_LIMIT = 175;
const TITLE_MAX_LENGTH = 75;

// 解码常见 HTML 实体（对齐 PHP html_entity_decode 的常用场景）
const decodeEntities = (str) =>
  str
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;|&apos;|&#x27;/gi, "'")
    .replace(/&#(\d+);/g, (match, code) => {
      const num = Number(code);
      return num > 0 && num <= 0x10ffff ? String.fromCodePoint(num) : match;
    })
    .replace(/&#x([0-9a-f]+);/gi, (match, code) => {
      const num = parseInt(code, 16);
      return num > 0 && num <= 0x10ffff ? String.fromCodePoint(num) : match;
    })
    .replace(/&amp;/gi, '&');

/**
 * 截取描述：去除 HTML 标签后提取纯文本，截取到 limit 附近，
 * 保证最后一个单词完整（从 limit 位置往后找到下一个空格处截断，结果可略超 limit）
 */
export function truncateDesc(html, limit = DEFAULT_DESC_LIMIT) {
  if (!html) return '';
  // 先移除 style/script 块和注释，避免其中的 CSS/JS 文本被当作正文截取
  const text = decodeEntities(
    String(html)
      .replace(/<(style|script)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
      .replace(/<!--[\s\S]*?-->/g, ' ')
      .replace(/<[^>]*>/g, ' ')
  )
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';
  if (text.length <= limit) return text;
  // 从 limit 位置往后找下一个空格，保证最后一个单词完整（结果接近 limit，可能略超）
  const nextSpace = text.indexOf(' ', limit);
  const cutAt = nextSpace > 0 ? nextSpace : text.length;
  return text.slice(0, cutAt).trimEnd();
}

/**
 * 截取页面标题：长度超过 TITLE_MAX_LENGTH 字符时，在 TITLE_MAX_LENGTH 范围内
 * 按最近的单词边界截断，保证单词完整，并追加 "..."
 * 按 UTF-8 字符（码点）计数
 */
export function truncateTitle(title) {
  const value = title || '';
  const chars = [...value];
  if (chars.length <= TITLE_MAX_LENGTH) {
    return value;
  }
  // 截取前 TITLE_MAX_LENGTH 字符，在最近的单词边界处断开，保证单词完整
  const truncated = chars.slice(0, TITLE_MAX_LENGTH).join('');
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  const cutAt = lastSpaceIndex > 0 ? lastSpaceIndex : TITLE_MAX_LENGTH;
  return truncated.slice(0, cutAt).trimEnd();
}

/**
 * 获取当前页面规范链接（移植自 PHP get_canonical_url）
 * 去掉查询参数和 hash，仅保留 origin + path；
 * SSR 环境优先使用 proxy-ssr 注入的真实请求 host（sitecoreContext.host，
 * 来自 x-forwarded-host / Host 头），缺失时才回退到构建时常量 sitecoreApiHost
 */
export function getCanonicalUrl(routePath = '') {
  if (canUseDOM()) {
    return `${window.location.origin}${window.location.pathname}`;
  }
  const path =
    String(routePath || '')
      .split('#')[0]
      .split('?')[0] || '/';
  const ssrHost = store.state.sitecoreContext?.host;
  return concatUrls(ssrHost || config.sitecoreApiHost, path);
}
